import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { Resolver, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { isSandboxEnvironment } from 'utils/sandbox/isSandboxEnvironment';

import type {
  AuthSchemeWithSecret,
  PostIntegrationAuthSchemesBody,
  PostIntegrationAuthSchemesSignBody
} from 'api';
import {
  getGetIntegrationAuthSchemesQueryKey,
  usePostIntegrationAuthSchemes,
  usePostIntegrationAuthSchemesSign
} from 'api';
import type { CreateKeyFormValues } from 'validators/createKeyModal';
import { createKeyModalSchema } from 'validators/createKeyModal';

import { useOrganisationId } from 'hooks/context';

import type { AuthMethod, UploadConfig } from './constants';
import {
  CREATE_KEY_FILE_TOO_LARGE,
  CREATE_KEY_FILE_WRONG_FORMAT,
  CREATE_KEY_GENERIC_ERROR,
  getUploadConfig,
  isAcceptedFileFormat
} from './constants';

type UseCreateKeyFormArgs = {
  onClose: () => void;
};

type CreatedResponse = { data: AuthSchemeWithSecret };

type CreateKeyPayload =
  | { kind: 'jwt'; data: PostIntegrationAuthSchemesSignBody }
  | { kind: 'pkcs'; data: PostIntegrationAuthSchemesBody };

export const buildCreateKeyPayload = (
  values: CreateKeyFormValues,
  isSandbox: boolean
): CreateKeyPayload => {
  const name = values.name.trim();
  const content = values.upload?.content ?? '';

  if (values.method === 'jwt') {
    return { kind: 'jwt', data: { name, certificate: content } };
  }

  const encryptionOn = isSandbox ? values.encryptionEnabled : true;
  if (!encryptionOn) {
    return { kind: 'pkcs', data: { name } };
  }

  return {
    kind: 'pkcs',
    data: { name, encryption_enabled: true, csr: content }
  };
};

export type UseCreateKeyForm = {
  form: UseFormReturn<CreateKeyFormValues>;
  submit: ReturnType<UseFormReturn<CreateKeyFormValues>['handleSubmit']>;
  cancel: () => void;
  onFileSelected: (file: File) => Promise<void>;
  removeUpload: () => void;
  setMethod: (value: AuthMethod) => void;
  setEncryptionEnabled: (value: boolean) => void;
  createdScheme: AuthSchemeWithSecret | null;
  isSubmitting: boolean;
  method: AuthMethod;
  showEncryptionToggle: boolean;
  requiresUpload: boolean;
  uploadConfig: UploadConfig;
};

const collectStrings = (value: unknown): string[] => {
  if (typeof value === 'string') {
    return [value];
  }
  if (Array.isArray(value)) {
    return (value as unknown[]).flatMap(collectStrings);
  }
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap(
      collectStrings
    );
  }
  return [];
};

const extractCreateKeyError = (error: unknown): string => {
  const body = (error ?? {}) as Record<string, unknown>;
  const data = (body.response as { data?: unknown } | undefined)?.data ?? body;
  const envelope = ((data as { error?: unknown }).error ?? data) as {
    title?: string;
    details?: unknown;
  };

  const messages = collectStrings(envelope.details);
  if (messages.length > 0) {
    return messages.join('. ');
  }
  return typeof envelope.title === 'string' && envelope.title.length > 0
    ? envelope.title
    : CREATE_KEY_GENERIC_ERROR;
};

export const useCreateKeyForm = ({
  onClose
}: UseCreateKeyFormArgs): UseCreateKeyForm => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const pkcsMutation = usePostIntegrationAuthSchemes();
  const signMutation = usePostIntegrationAuthSchemesSign();

  const [createdScheme, setCreatedScheme] =
    useState<AuthSchemeWithSecret | null>(null);

  const form = useForm<CreateKeyFormValues>({
    mode: 'onChange',
    resolver: yupResolver(
      createKeyModalSchema
    ) as unknown as Resolver<CreateKeyFormValues>,
    defaultValues: {
      name: '',
      method: 'pkcs',
      encryptionEnabled: true,
      upload: null,
      acknowledge: false
    }
  });

  const method = form.watch('method');
  const encryptionEnabled = form.watch('encryptionEnabled');

  const isSandbox = isSandboxEnvironment();
  const effectiveEncryptionEnabled =
    method === 'pkcs' ? (isSandbox ? encryptionEnabled : true) : false;
  const showEncryptionToggle = isSandbox && method === 'pkcs';
  const requiresUpload =
    method === 'jwt' || (method === 'pkcs' && effectiveEncryptionEnabled);
  const uploadConfig = getUploadConfig(method);

  const handleSuccess = (response: CreatedResponse) => {
    queryClient.invalidateQueries({
      queryKey: getGetIntegrationAuthSchemesQueryKey(organisationId)
    });
    setCreatedScheme(response.data);
  };

  const handleError = (error: unknown) => {
    openNotification({ kind: 'error', message: extractCreateKeyError(error) });
  };

  const submit = form.handleSubmit((values) => {
    const options = { onSuccess: handleSuccess, onError: handleError };
    const payload = buildCreateKeyPayload(values, isSandbox);

    if (payload.kind === 'jwt') {
      signMutation.mutate({ organisationId, data: payload.data }, options);
      return;
    }

    pkcsMutation.mutate({ organisationId, data: payload.data }, options);
  });

  const clearUpload = () =>
    form.setValue('upload', null, { shouldValidate: true });

  const onFileSelected = async (file: File) => {
    if (!isAcceptedFileFormat(file.name, uploadConfig.accept)) {
      openNotification({
        kind: 'error',
        message: CREATE_KEY_FILE_WRONG_FORMAT
      });
      return;
    }
    if (file.size > uploadConfig.maxBytes) {
      openNotification({ kind: 'error', message: CREATE_KEY_FILE_TOO_LARGE });
      return;
    }
    const content = await file.text();
    form.setValue(
      'upload',
      { name: file.name, size: file.size, content },
      { shouldValidate: true }
    );
  };

  const setMethod = (value: AuthMethod) => {
    form.setValue('method', value, { shouldValidate: true });
    clearUpload();
  };

  const setEncryptionEnabled = (value: boolean) => {
    form.setValue('encryptionEnabled', value, { shouldValidate: true });
    clearUpload();
  };

  const cancel = () => {
    form.reset();
    setCreatedScheme(null);
    onClose();
  };

  return {
    form,
    submit,
    cancel,
    onFileSelected,
    removeUpload: clearUpload,
    setMethod,
    setEncryptionEnabled,
    createdScheme,
    isSubmitting: pkcsMutation.isPending || signMutation.isPending,
    method,
    showEncryptionToggle,
    requiresUpload,
    uploadConfig
  };
};
